import { Input, message, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { appConfigAdminStore } from '@/stores/appConfigAdminStore';
import type { DesktopUpdateApproval, PolicyUserIdentity } from '@/types';
import {
  buildDesktopFeatureApprovalChanges,
  DESKTOP_UPDATE_CHANNELS,
  desktopUpdateUserLabel,
} from './desktopUpdatePolicyModel';

export function formatDesktopUpdateDateTime(value: string) {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

function publicationDescription(approval: DesktopUpdateApproval) {
  const publication = approval.publication;
  if (!publication) {
    return approval.status === 'approved' ? '等待后台发布任务接管' : '';
  }
  if (publication.status === 'published') {
    return `AppConfig v${publication.hostedVersion} 已发布`;
  }
  if (publication.status === 'failed') {
    return `发布失败，系统将在 ${formatDesktopUpdateDateTime(publication.nextAttemptAt)} 自动重试：${publication.lastError}`;
  }
  if (publication.status === 'deploying') {
    return `AppConfig v${publication.hostedVersion} 正在部署（${publication.deploymentState || '处理中'}）`;
  }
  if (publication.status === 'superseded') {
    return '发布任务已被后续配置替代';
  }
  return '发布任务等待执行';
}

const featureChangePresentation = {
  added: { label: '新增', color: 'green' },
  removed: { label: '删除', color: 'red' },
  modified: { label: '修改', color: 'blue' },
} as const;

function DesktopFeatureApprovalChanges({
  approval,
}: {
  approval: DesktopUpdateApproval;
}) {
  const changes = buildDesktopFeatureApprovalChanges(
    approval.baselineProduct,
    approval.targetProduct,
  );
  if (!changes.length) {
    return (
      <div className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground">
        Feature 白名单无变更
      </div>
    );
  }
  const counts = changes.reduce(
    (result, change) => {
      result[change.kind] += 1;
      return result;
    },
    { added: 0, removed: 0, modified: 0 },
  );
  const summary = [
    counts.added ? `新增 ${counts.added}` : '',
    counts.modified ? `修改 ${counts.modified}` : '',
    counts.removed ? `删除 ${counts.removed}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <details
      className="group rounded-md border border-border"
      open={approval.status === 'pending'}
    >
      <summary className="cursor-pointer select-none px-3 py-2 font-medium">
        Feature 白名单变更（{summary}）
      </summary>
      <div className="space-y-2 border-t border-border p-3">
        {changes.map((change, index) => {
          const presentation = featureChangePresentation[change.kind];
          return (
            <div
              key={change.key}
              className="space-y-1 rounded-md bg-muted/30 p-3 text-xs"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  {index + 1}. {change.key}
                </span>
                <Tag color={presentation.color}>{presentation.label}</Tag>
                <span className="text-muted-foreground">
                  {change.description}
                </span>
              </div>
              {change.details.map((detail) => (
                <div key={detail} className="break-words text-muted-foreground">
                  {detail}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </details>
  );
}

export const DesktopUpdateApprovalList = observer(
  ({
    approvals,
    canWrite,
    canApprove,
    currentUserId,
    approverUids,
    userIdentities,
    hasMore,
  }: {
    approvals: DesktopUpdateApproval[];
    canWrite: boolean;
    canApprove: boolean;
    currentUserId?: string;
    approverUids: string[];
    userIdentities: Record<string, PolicyUserIdentity>;
    hasMore: boolean;
  }) => {
    const [rejectionReasons, setRejectionReasons] = useState<
      Record<string, string>
    >({});

    return (
      <section className="space-y-4 rounded-lg border border-border bg-background p-6 shadow-sm">
        <h2 className="text-base font-semibold">审批与审计记录</h2>
        {approvals.map((approval) => {
          const eligibleApprovers = approverUids
            .filter((userID) => userID !== approval.submitterUid)
            .map((userID) => desktopUpdateUserLabel(userIdentities, userID));
          return (
            <article
              key={approval.id}
              className="space-y-3 rounded-md border border-border p-4 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <strong>
                  #{approval.id} · {approval.product}
                </strong>
                <Tag color={approval.status === 'pending' ? 'gold' : 'default'}>
                  {approval.status}
                </Tag>
                <span className="text-muted-foreground">
                  {formatDesktopUpdateDateTime(approval.createdAt)} · 提交人{' '}
                  {desktopUpdateUserLabel(
                    userIdentities,
                    approval.submitterUid,
                  )}
                </span>
              </div>
              <p>{approval.reason}</p>
              <div className="text-xs font-medium">目标最低版本配置</div>
              <div className="grid gap-2 rounded-md bg-muted/30 p-3 text-xs md:grid-cols-2">
                {DESKTOP_UPDATE_CHANNELS.map((channel) => (
                  <div key={channel}>
                    <span className="font-medium uppercase">{channel}</span>
                    {' 统一版本 '}
                    {approval.targetProduct.channels[channel]
                      .defaultMinimumVersion || '未设置'}
                    {Object.entries(
                      approval.targetProduct.channels[channel].targetOverrides,
                    ).map(([target, version]) => (
                      <span key={target}>
                        {' · '}
                        {target} {version}
                      </span>
                    ))}
                  </div>
                ))}
                <div>
                  过期时间：
                  {formatDesktopUpdateDateTime(approval.expiresAt)}
                </div>
              </div>
              <DesktopFeatureApprovalChanges approval={approval} />
              <div className="text-xs text-muted-foreground">
                基线 v{approval.baselineVersion} · 目标哈希{' '}
                {approval.targetHash}
                {approval.approverUid
                  ? ` · 审批人 ${desktopUpdateUserLabel(
                      userIdentities,
                      approval.approverUid,
                    )}`
                  : ''}
                {approval.rejectionReason
                  ? ` · 拒绝原因 ${approval.rejectionReason}`
                  : ''}
              </div>
              {approval.status === 'pending' ? (
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground">
                    可审批人：
                    {eligibleApprovers.length
                      ? eligibleApprovers.join('、')
                      : '没有符合“非提交人”要求的固定审批人'}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {canWrite &&
                    canApprove &&
                    approval.submitterUid !== currentUserId ? (
                      <>
                        <Button
                          onClick={() =>
                            void appConfigAdminStore
                              .approveDesktopUpdate(approval.id)
                              .then((updated) =>
                                updated.status === 'published'
                                  ? message.success('已通过并发布')
                                  : message.info('已通过，系统正在发布'),
                              )
                              .catch((error) =>
                                message.error(
                                  error instanceof Error
                                    ? error.message
                                    : '审批失败',
                                ),
                              )
                          }
                        >
                          通过
                        </Button>
                        <Input
                          className="max-w-sm"
                          value={rejectionReasons[approval.id] || ''}
                          onChange={(event) =>
                            setRejectionReasons((current) => ({
                              ...current,
                              [approval.id]: event.target.value,
                            }))
                          }
                          placeholder="拒绝原因（必填）"
                        />
                        <Button
                          variant="secondary"
                          disabled={!rejectionReasons[approval.id]?.trim()}
                          onClick={() =>
                            void appConfigAdminStore
                              .rejectDesktopUpdate(
                                approval.id,
                                rejectionReasons[approval.id],
                              )
                              .then(() => message.success('已拒绝'))
                              .catch((error) =>
                                message.error(
                                  error instanceof Error
                                    ? error.message
                                    : '拒绝失败',
                                ),
                              )
                          }
                        >
                          拒绝
                        </Button>
                      </>
                    ) : null}
                    {canWrite && approval.submitterUid === currentUserId ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void appConfigAdminStore
                            .withdrawDesktopUpdate(approval.id)
                            .then(() => message.success('已撤回'))
                            .catch((error) =>
                              message.error(
                                error instanceof Error
                                  ? error.message
                                  : '撤回失败',
                              ),
                            )
                        }
                      >
                        撤回
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {approval.status === 'approved' || approval.publication ? (
                <div className="text-xs text-muted-foreground">
                  {publicationDescription(approval)}
                </div>
              ) : null}
            </article>
          );
        })}
        {!approvals.length ? (
          <div className="text-sm text-muted-foreground">暂无审批记录</div>
        ) : null}
        {hasMore ? (
          <Button
            variant="secondary"
            disabled={appConfigAdminStore.isDesktopLoading()}
            onClick={() =>
              void appConfigAdminStore
                .fetchMoreDesktopUpdateApprovals()
                .catch((error) =>
                  message.error(
                    error instanceof Error ? error.message : '加载失败',
                  ),
                )
            }
          >
            加载更多
          </Button>
        ) : null}
      </section>
    );
  },
);
